/**
 * DashboardNavbar — Top bar for authenticated pages
 *
 * Shows breadcrumb, dark-mode toggle, settings gear icon,
 * role display, and user menu.
 */

import { useState, useContext, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";

import KDBox from "components/KDBox";
import KDTypography from "components/KDTypography";

import {
  useMaterialUIController,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";
import { AuthContext } from "context/AuthContext";
import { useT } from "context/I18nContext";

function DashboardNavbar({ absolute, light, isMini, breadcrumbLabel }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, darkMode } = controller;
  const { currentUser, activeRole, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const t = useT();

  const [userMenu, setUserMenu] = useState(null);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, true);

  const handleLogout = async () => {
    setUserMenu(null);
    await logout();
    navigate("/login");
  };

  // Breadcrumb from pathname — last segment can be overridden via breadcrumbLabel
  const segments = pathname.split("/").filter(Boolean);
  const displaySegments = useMemo(() => {
    // Translate a breadcrumb segment — tries breadcrumb.{segment}, falls back to formatted segment
    const translateSegment = (segment) => {
      const key = `breadcrumb.${segment}`;
      const translated = t(key);
      // If translation returns the key itself, use formatted segment
      return translated === key ? segment.replace(/-/g, " ") : translated;
    };
    
    const translatedSegments = segments.map(translateSegment);
    if (breadcrumbLabel) {
      return [...translatedSegments.slice(0, -1), breadcrumbLabel];
    }
    return translatedSegments;
  }, [segments, breadcrumbLabel, t]);
  
  const pageTitle = displaySegments[displaySegments.length - 1] || t("breadcrumb.dashboard");

  // Navbar colors
  const iconsColor = light || darkMode ? "white" : "dark";

  return (
    <AppBar
      position={absolute ? "absolute" : fixedNavbar ? "sticky" : "static"}
      color="inherit"
      sx={{
        boxShadow: "none",
        backdropFilter: transparentNavbar ? "saturate(200%) blur(30px)" : "none",
        backgroundColor: transparentNavbar
          ? darkMode
            ? "rgba(15,15,26,0.8)"
            : "rgba(255,255,255,0.8)"
          : darkMode
            ? "background.default"
            : "#fff",
        borderBottom: transparentNavbar ? "none" : "1px solid",
        borderColor: darkMode ? "rgba(255,255,255,0.08)" : "grey.200",
        top: 0,
        zIndex: 1100,
      }}
    >
      <Toolbar sx={{ display: "flex", justifyContent: "space-between", px: 2, minHeight: 56 }}>
        {/* Left: hamburger + breadcrumb */}
        <KDBox display="flex" alignItems="center" gap={1}>
          <IconButton size="small" color="inherit" onClick={handleMiniSidenav}>
            <Icon sx={{ color: iconsColor }}>{miniSidenav ? "menu_open" : "menu"}</Icon>
          </IconButton>

          <KDBox>
            <KDTypography
              variant="caption"
              color={light ? "white" : "text"}
              fontWeight="medium"
              sx={{ textTransform: "capitalize" }}
            >
              {displaySegments.join(" / ")}
            </KDTypography>
            <KDTypography
              variant="h6"
              fontWeight="bold"
              color={light ? "white" : "dark"}
              sx={{ textTransform: "capitalize", lineHeight: 1 }}
            >
              {pageTitle}
            </KDTypography>
          </KDBox>
        </KDBox>

        {/* Right: actions */}
        {!isMini && (
          <KDBox display="flex" alignItems="center" gap={0.5}>
            {/* Settings gear — opens Configurator sidebar */}
            <Tooltip title={t("navbar.settings")}>
              <IconButton size="small" onClick={handleConfiguratorOpen}>
                <Icon sx={{ color: iconsColor }}>settings</Icon>
              </IconButton>
            </Tooltip>

            {/* Role indicator */}
            {activeRole && (
              <KDTypography
                variant="button"
                fontWeight="medium"
                color={iconsColor}
                sx={{ textTransform: "capitalize", mx: 1 }}
              >
                {t(`role.${activeRole}`, { defaultMessage: activeRole })}
              </KDTypography>
            )}

            {/* User menu */}
            <Tooltip title={t("navbar.account")}>
              <IconButton size="small" onClick={(e) => setUserMenu(e.currentTarget)}>
                <Icon sx={{ color: iconsColor }}>account_circle</Icon>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={userMenu}
              open={Boolean(userMenu)}
              onClose={() => setUserMenu(null)}
            >
              <MenuItem disabled>
                <KDTypography variant="caption">
                  {currentUser?.email || "—"}
                </KDTypography>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <Icon sx={{ mr: 1 }}>logout</Icon> {t("navbar.logout")}
              </MenuItem>
            </Menu>
          </KDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

export default DashboardNavbar;
